import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

import type {
  BdcCompanyDataResponse,
  BdcCompanyDataValues,
} from "../../../api/bdc.api";
import { BDC_COMPANY_DATA_KEYS } from "../../../api/bdc.api";
import {
  useBdcCompanyData,
  useConfirmBdcCompanyData,
} from "../../../hooks/vault/queries/useBdcCompanyData";
import { useBdcStatus } from "../../../hooks/vault/queries/useBdcStatus";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  onSetAddressFields,
  onUnsetAddressFields,
} from "../../../redux/actions/requirementsActions";
import {
  Status,
  type Folder,
  type FormValue,
  type Requirement,
} from "../../../redux/interfaces/folder.types";
import { Button } from "../../ui/button";
import { BdcStatusCards } from "../documents/bdc/BdcStatusCards";
import {
  isBdcAuthorizationRequirement,
  shouldValidateNativeBdcCompanyData,
} from "../utils/requirementGrouping";
import {
  isBdcActorMismatch,
  isNativeBdcSupported,
  resolveBdcExpectedActor,
} from "../../../utils/vault/bdcActor";
import { allFormsValidators } from "../../../utils/vault/forms/allFormsValidators";
import {
  BDC_COMPANY_ADDRESS_LENGTH_ERROR,
  validateBdcCompanyData,
} from "../../../utils/vault/bdcInputValidation";
import { DynamicFormBuilder } from "./DynamicFormBuilder";
import {
  isDeterministicBdcError,
  mapBdcAxiosError,
} from "../../../exceptions/creditReportExceptions";

type ViewMode = "confirmation" | "edit";

interface BdcCompanyDataStepProps {
  readonly header: ReactNode;
  readonly onRegisterSubmit?: (
    submitFn: (() => Promise<boolean>) | null,
  ) => void;
  readonly isAdminContext?: boolean;
  readonly forceEdit?: boolean;
  readonly onForceEditConsumed?: () => void;
}

const EMPTY_VALUES: BdcCompanyDataValues = {
  business_legal_name: "",
  rfc_company: "",
  street: "",
  exterior_number: "",
  interior_number: "",
  postal_code: "",
  neighborhood: "",
  municipality: "",
  state: "",
};

type BdcCompanyField = FormValue & { key: keyof BdcCompanyDataValues };

// These are used only when a legacy PM folio has no sibling business-form
// definition. Normal folios use that requirement's live field metadata below,
// so placeholders, input types and select options stay aligned with the
// existing Formulario de la Empresa instead of being reimplemented here.
const FALLBACK_FIELDS: readonly BdcCompanyField[] = [
  {
    order: 1,
    key: "business_legal_name",
    label: "Razón social",
    required: true,
    type: "input",
    value: "",
    placeholder: "Ej: Mi negocio S.A. de C.V.",
    options: null,
  },
  {
    order: 2,
    key: "rfc_company",
    label: "RFC de la empresa",
    required: true,
    type: "input",
    value: "",
    placeholder: "Ej: EXT990101NI1",
    options: null,
  },
  {
    order: 3,
    key: "street",
    label: "Calle",
    required: true,
    type: "input",
    value: "",
    placeholder: "Ej: Av. Vallarta",
    options: null,
  },
  {
    order: 4,
    key: "exterior_number",
    label: "Número exterior",
    required: true,
    type: "input",
    value: "",
    placeholder: "Ej: 742",
    options: null,
  },
  {
    order: 5,
    key: "interior_number",
    label: "Número interior (opcional)",
    required: false,
    type: "input",
    value: "",
    placeholder: "Ej: 2",
    options: null,
  },
  {
    order: 6,
    key: "postal_code",
    label: "Código postal",
    required: true,
    type: "number",
    value: "",
    placeholder: "Ej: 44160",
    options: null,
  },
  {
    order: 7,
    key: "neighborhood",
    label: "Colonia",
    required: true,
    type: "select",
    value: "",
    placeholder: "Ej: Americana",
    options: [],
  },
  {
    order: 8,
    key: "municipality",
    label: "Municipio o alcaldía",
    required: true,
    type: "input",
    value: "",
    placeholder: "Ej: Guadalajara",
    options: null,
  },
  {
    order: 9,
    key: "state",
    label: "Estado",
    required: true,
    type: "select",
    value: "",
    placeholder: "Ej: Jalisco",
    options: [
      "Aguascalientes",
      "Baja California",
      "Baja California Sur",
      "Campeche",
      "Chiapas",
      "Chihuahua",
      "Coahuila",
      "Colima",
      "Durango",
      "Guanajuato",
      "Guerrero",
      "Hidalgo",
      "Jalisco",
      "Estado de México",
      "Ciudad de México",
      "Michoacán",
      "Morelos",
      "Nayarit",
      "Nuevo León",
      "Oaxaca",
      "Puebla",
      "Querétaro",
      "Quintana Roo",
      "San Luis Potosí",
      "Sinaloa",
      "Sonora",
      "Tabasco",
      "Tamaulipas",
      "Tlaxcala",
      "Veracruz",
      "Yucatán",
      "Zacatecas",
    ],
  },
];

const REQUIRED_FIELDS: Record<string, boolean> = Object.fromEntries(
  FALLBACK_FIELDS.map((field) => [field.key, field.required]),
);
const DISPLAY_FIELDS: Record<string, "block" | "hidden"> = Object.fromEntries(
  FALLBACK_FIELDS.map((field) => [field.key, "block"]),
);
const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  FALLBACK_FIELDS.map((field) => [field.key, field.label]),
);
const COMPANY_FIELD_KEYS = new Set<string>(BDC_COMPANY_DATA_KEYS);

function getCompanyFields(
  activeSubfolder: ReturnType<typeof getActiveSubfolder>,
): readonly BdcCompanyField[] {
  const businessForm = activeSubfolder?.requirements.find(
    (requirement) => requirement.slug === "business-form",
  );
  const sourceFields = businessForm?.form_values ?? [];
  const sourceByKey = new Map(sourceFields.map((field) => [field.key, field]));

  return FALLBACK_FIELDS.map((fallback) => {
    const source = sourceByKey.get(fallback.key);
    return {
      ...fallback,
      type: source?.type ?? fallback.type,
      placeholder: source?.placeholder || fallback.placeholder,
      options: source?.options ?? fallback.options,
      tooltip: source?.tooltip ?? fallback.tooltip,
    };
  });
}

function getServerFieldErrors(error: unknown): Record<string, string> {
  if (!error || typeof error !== "object") return {};
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") return {};
  const data = (response as { data?: unknown }).data;
  if (!data || typeof data !== "object") return {};
  const fieldErrors = (data as { field_errors?: unknown }).field_errors;
  if (!fieldErrors || typeof fieldErrors !== "object") return {};

  const result: Record<string, string> = {};
  for (const [key, message] of Object.entries(fieldErrors)) {
    if (COMPANY_FIELD_KEYS.has(key) && typeof message === "string") {
      result[key] = message;
    }
  }
  return result;
}

/**
 * Staff requests deliberately cannot call the actor-only BDC API. Internal
 * folder responses already contain the reviewed company snapshot on the BDC
 * requirement, so normalize that snapshot for the safe read-only view.
 */
function getCompanyDataSnapshot(
  requirement: Requirement | undefined,
): Readonly<BdcCompanyDataResponse> | undefined {
  const snapshot = requirement?.verification_data?.bdc?.company_data;
  if (!snapshot || typeof snapshot !== "object") return undefined;

  const rawValues =
    snapshot.values && typeof snapshot.values === "object"
      ? snapshot.values
      : {};
  return {
    values: { ...EMPTY_VALUES, ...rawValues },
    reviewed: snapshot.reviewed === true,
    source:
      typeof snapshot.source === "string" ? snapshot.source : "bdc_snapshot",
    reviewed_at:
      typeof snapshot.reviewed_at === "string" || snapshot.reviewed_at === null
        ? snapshot.reviewed_at
        : null,
  };
}

function getActiveSubfolder(
  activeRequirement: Requirement | null,
  activeVault: Folder | null,
) {
  if (!activeVault?.subfolders || !activeRequirement) return undefined;
  return activeVault.subfolders.find(
    (candidate) =>
      candidate.id === activeRequirement.subfolder ||
      candidate.slug === activeRequirement.subfolder ||
      candidate.requirements.some(
        (requirement) => requirement.id === activeRequirement.id,
      ),
  );
}

export function BdcCompanyDataStep({
  header,
  onRegisterSubmit,
  isAdminContext,
  forceEdit = false,
  onForceEditConsumed,
}: BdcCompanyDataStepProps) {
  const dispatch = useAppDispatch();
  const { activeRequirement, activeVault, jwtToken } = useAppSelector(
    (state) => state.vault,
  );

  // This component is also used by actor-side unit/legacy mounts without an
  // explicit option. Only FormView's explicit flag may switch it to staff
  // mode; inferring from a generic pathname would make tests or embedded vault
  // mounts accidentally read-only.
  const effectiveIsAdminContext = isAdminContext === true;

  const activeSubfolder = useMemo(
    () => getActiveSubfolder(activeRequirement, activeVault),
    [activeRequirement, activeVault],
  );
  const bdcRequirement = activeSubfolder?.requirements.find((requirement) =>
    isBdcAuthorizationRequirement(requirement.slug, requirement.type),
  );
  const nativeBdcSupported = isNativeBdcSupported(
    bdcRequirement,
    activeSubfolder,
  );
  const companyFields = useMemo(
    () => getCompanyFields(activeSubfolder),
    [activeSubfolder],
  );
  const isParticipantMismatch =
    !effectiveIsAdminContext &&
    isBdcActorMismatch(jwtToken, activeSubfolder?.slug);
  const bdcStatusQuery = useBdcStatus(bdcRequirement?.id, {
    enabled: nativeBdcSupported && !isParticipantMismatch,
    isAdminContext: effectiveIsAdminContext,
    staleTime: 30_000,
  });
  const strictBdcCompanyValidation =
    nativeBdcSupported &&
    shouldValidateNativeBdcCompanyData(
      bdcRequirement,
      bdcStatusQuery.data,
    );
  const staffSnapshot = useMemo(
    () => getCompanyDataSnapshot(bdcRequirement),
    [bdcRequirement],
  );

  // Staff reads use the session/cookie client; only the actor render can reach
  // the mutating confirmation hook below. A staff render is always read-only.
  const query = useBdcCompanyData(bdcRequirement?.id, {
    isAdminContext: effectiveIsAdminContext,
    enabled: !isParticipantMismatch,
  });
  const mutation = useConfirmBdcCompanyData(activeVault?.id);
  const [values, setValues] = useState<BdcCompanyDataValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [neighborhoodOptions, setNeighborhoodOptions] = useState<string[]>([]);
  const [mode, setMode] = useState<ViewMode>("edit");
  const initializedResponseRef = useRef<string | null>(null);
  const postalLookupSequenceRef = useRef(0);
  const companySelectOptions = useMemo(
    () => ({ neighborhood: neighborhoodOptions }),
    [neighborhoodOptions],
  );

  const response =
    query.data ?? (effectiveIsAdminContext ? staffSnapshot : undefined);

  const isAuthorizationTerminal =
    bdcRequirement?.status === Status.Valid ||
    bdcRequirement?.status === Status.PendingReview;
  const canEdit =
    !effectiveIsAdminContext &&
    !isParticipantMismatch &&
    response?.locked !== true &&
    !isAuthorizationTerminal;

  useEffect(() => {
    if (!response || !bdcRequirement) return;
    const responseKey = `${bdcRequirement.id}:${response.reviewed}:${response.locked === true}:${
      response.reviewed_at ?? response.source
    }:${JSON.stringify(response.values)}`;
    if (initializedResponseRef.current === responseKey) return;
    initializedResponseRef.current = responseKey;
    const nextValues = { ...EMPTY_VALUES };
    for (const key of BDC_COMPANY_DATA_KEYS) {
      nextValues[key] = String(response.values?.[key] ?? "");
    }
    nextValues.rfc_company = nextValues.rfc_company.toUpperCase();
    setValues(nextValues);
    // Keep colonia choices local to this participant. The generic form stores
    // address options globally, which must not leak into another actor's BDC
    // step while switching tabs.
    setNeighborhoodOptions(
      nextValues.neighborhood ? [nextValues.neighborhood] : [],
    );
    postalLookupSequenceRef.current += 1;
    setMode(
      response.reviewed && canEdit
        ? "confirmation"
        : canEdit
          ? "edit"
          : "confirmation",
    );
  }, [bdcRequirement, canEdit, response]);

  // A terminal authorization wins over stale child state (for example a
  // pending query that previously rendered the edit form).
  useEffect(() => {
    if (!canEdit) setMode("confirmation");
  }, [canEdit]);

  useEffect(() => {
    if (!forceEdit || query.isLoading || !response) return;
    if (canEdit) setMode("edit");
    onForceEditConsumed?.();
  }, [canEdit, forceEdit, onForceEditConsumed, query.isLoading, response]);

  const validateField = useCallback(
    async (key: keyof BdcCompanyDataValues) => {
      const field = companyFields.find((candidate) => candidate.key === key);
      if (!field) return true;
      const value = values[field.key].trim();
      let message: string | undefined;

      if (field.required && !value) {
        message = "Este campo es obligatorio";
      } else {
        const validator = allFormsValidators[field.key];
        if (validator && value) {
          try {
            await validator(null, value);
          } catch (error) {
            message =
              error instanceof Error ? error.message : "El valor no es válido";
          }
        }
      }

      const strictErrors = strictBdcCompanyValidation
        ? validateBdcCompanyData(
            values as unknown as Record<string, unknown>,
          )
        : {};
      if (strictErrors[key]) message = strictErrors[key];

      setErrors((current) => {
        const next = { ...current };
        if (message) next[key] = message;
        else delete next[key];
        if (strictBdcCompanyValidation && key !== "street") {
          if (strictErrors.street) next.street = strictErrors.street;
          else if (
            next.street === BDC_COMPANY_ADDRESS_LENGTH_ERROR &&
            (key === "exterior_number" || key === "interior_number")
          ) {
            delete next.street;
          }
        }
        return next;
      });
      return !message;
    },
    [companyFields, strictBdcCompanyValidation, values],
  );

  const validate = useCallback(async () => {
    const nextErrors: Record<string, string> = {};
    for (const field of companyFields) {
      const value = values[field.key].trim();
      if (field.required && !value) {
        nextErrors[field.key] = "Este campo es obligatorio";
        continue;
      }

      const validator = allFormsValidators[field.key];
      if (!validator || !value) continue;
      try {
        await validator(null, value);
      } catch (error) {
        nextErrors[field.key] =
          error instanceof Error ? error.message : "El valor no es válido";
      }
    }
    if (strictBdcCompanyValidation) {
      Object.assign(
        nextErrors,
        validateBdcCompanyData(values as unknown as Record<string, unknown>),
      );
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [companyFields, strictBdcCompanyValidation, values]);

  const handleFieldChange = useCallback(
    (key: string, nextValue: unknown) => {
      if (!COMPANY_FIELD_KEYS.has(key)) return;
      const companyKey = key as keyof BdcCompanyDataValues;
      const value = String(nextValue ?? "");

      setValues((current) => ({ ...current, [companyKey]: value }));
      setErrors((current) => {
        if (!current[companyKey]) return current;
        const next = { ...current };
        delete next[companyKey];
        return next;
      });

      if (companyKey !== "postal_code") return;
      const lookupSequence = ++postalLookupSequenceRef.current;
      if (value.length === 5) {
        void dispatch(
          onSetAddressFields({ form: null, zipCode: value, field: companyKey }),
        )
          .unwrap()
          .then((result) => {
            if (
              postalLookupSequenceRef.current !== lookupSequence ||
              !result?._address
            ) {
              return;
            }
            const options = Array.isArray(result.neighborhood)
              ? result.neighborhood.map(String)
              : [];
            setNeighborhoodOptions(options);
            setValues((current) => {
              // Ignore a lookup that completed after the participant changed CP.
              if (current.postal_code !== value) return current;
              return {
                ...current,
                neighborhood: String(result._address.neighborhood ?? ""),
                municipality: String(result._address.municipality ?? ""),
                state: String(result._address.state ?? ""),
              };
            });
            setErrors((current) => {
              const next = { ...current };
              delete next.neighborhood;
              delete next.municipality;
              delete next.state;
              return next;
            });
          })
          .catch(() => undefined);
      } else {
        setNeighborhoodOptions([]);
        void dispatch(
          onUnsetAddressFields({
            form: null,
            zipCode: value,
            field: companyKey,
          }),
        );
        setValues((current) => ({
          ...current,
          neighborhood: "",
          municipality: "",
          state: "",
        }));
      }
    },
    [dispatch],
  );

  const submit = useCallback(async () => {
    if (!canEdit || !bdcRequirement || !(await validate())) return false;
    try {
      await mutation.mutateAsync({ requirementId: bdcRequirement.id, values });
      setMode("confirmation");
      toast.success("Datos de la empresa confirmados.");
      return true;
    } catch (error) {
      const fieldErrors = getServerFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((current) => ({ ...current, ...fieldErrors }));
        toast.error("Revisa los campos marcados antes de continuar.");
      } else {
        toast.error(
          "No pudimos guardar los datos de la empresa. Inténtalo de nuevo.",
        );
      }
      return false;
    }
  }, [bdcRequirement, canEdit, mutation, validate, values]);

  useEffect(() => {
    if (!onRegisterSubmit) return;
    if (
      effectiveIsAdminContext ||
      mode === "confirmation" ||
      !canEdit ||
      query.isLoading ||
      query.isError
    ) {
      onRegisterSubmit(null);
      return;
    }
    onRegisterSubmit(submit);
    return () => onRegisterSubmit(null);
  }, [
    canEdit,
    effectiveIsAdminContext,
    mode,
    onRegisterSubmit,
    query.isError,
    query.isLoading,
    submit,
  ]);

  if (isParticipantMismatch) {
    const participantLabel =
      resolveBdcExpectedActor(activeSubfolder?.slug) === "guarantor"
        ? "fiador"
        : "inquilino";
    return (
      <BdcStatusCards
        variant="read_only"
        title={`Datos de empresa del ${participantLabel}`}
        description={`Este paso corresponde al ${participantLabel}. Por seguridad, sólo esa persona puede revisar o confirmar los datos usados para su consulta a Buró de Crédito.`}
      />
    );
  }

  if (query.isLoading) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center text-center">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-[#28397a]" />
        <p className="text-sm text-slate-500">Cargando datos de la empresa…</p>
      </div>
    );
  }

  const hasStaffFallback = effectiveIsAdminContext && Boolean(staffSnapshot);
  if ((!hasStaffFallback && query.isError) || !bdcRequirement || !response) {
    const queryError = mapBdcAxiosError(query.error);
    const canRetry = !isDeterministicBdcError(queryError.code);
    return (
      <div
        className="flex min-h-64 flex-col items-center justify-center px-4 text-center"
        role="alert"
      >
        <AlertTriangle className="mb-3 h-8 w-8 text-amber-500" />
        <h3 className="text-lg font-semibold text-slate-900">
          No pudimos cargar los datos
        </h3>
        <p className="mt-1 max-w-md text-sm text-slate-500">
          {canRetry
            ? "Revisa tu conexión e inténtalo nuevamente."
            : queryError.message}
        </p>
        {canRetry && (
          <Button
            type="button"
            variant="outline"
            className="mt-5"
            onClick={() => void query.refetch()}
          >
            Reintentar
          </Button>
        )}
      </div>
    );
  }

  if (effectiveIsAdminContext || mode === "confirmation" || !canEdit) {
    return (
      <div className="py-10 px-4 sm:px-6">
        <div className="mx-auto max-w-2xl">
          <div className="mb-6 flex items-start gap-3">
            {response.reviewed ? (
              <CheckCircle
                className="mt-0.5 h-6 w-6 shrink-0 text-green-600"
                strokeWidth={1.5}
              />
            ) : (
              <Pencil
                className="mt-0.5 h-6 w-6 shrink-0 text-slate-500"
                strokeWidth={1.5}
              />
            )}
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {response.reviewed
                  ? "Datos de la empresa confirmados"
                  : "Datos de la empresa pendientes de confirmar"}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Esta información se utilizará exclusivamente para la consulta a
                Buró de Crédito.
              </p>
              {effectiveIsAdminContext && (
                <p className="mt-2 text-xs font-medium text-slate-500">
                  Vista de solo lectura para personal operativo.
                </p>
              )}
            </div>
          </div>
          <dl className="grid gap-x-8 gap-y-5 rounded-xl border border-slate-200 bg-slate-50 p-5 sm:grid-cols-2">
            {companyFields.map((field) => (
              <div key={field.key}>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {field.label}
                </dt>
                <dd className="mt-1 break-words text-sm font-medium text-slate-900">
                  {values[field.key] || "No proporcionado"}
                </dd>
              </div>
            ))}
          </dl>
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              className="mt-5 gap-2"
              onClick={() => setMode("edit")}
            >
              <Pencil className="h-4 w-4" />
              Editar datos
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-4">
      <div className="text-sm leading-6 text-slate-600">{header}</div>
      <p className="text-sm text-slate-500">
        Precargamos la información disponible en el formulario de la empresa.
        Revísala y corrige cualquier dato antes de continuar.
      </p>
      <div className="-mx-2 flex flex-wrap items-end">
        {companyFields.map((field) => (
          <DynamicFormBuilder
            key={field.key}
            formItem={field}
            value={values[field.key]}
            onChange={handleFieldChange}
            onBlur={(key) =>
              void validateField(key as keyof BdcCompanyDataValues)
            }
            requiredFields={REQUIRED_FIELDS}
            displayFields={DISPLAY_FIELDS}
            labelFields={FIELD_LABELS}
            disabled={mutation.isPending || !canEdit}
            errors={errors}
            dynamicOptions={companySelectOptions}
          />
        ))}
      </div>
    </div>
  );
}
